/**
 * 1.8.0 rights: the words the web app shows for a rights summary (the mark's tooltip, the pills, the Rights tabs).
 * Pure; the rules themselves are contracts rights.ts.
 */
import {
  RIGHTS_STATE_LABELS, RIGHTS_STATES, RIGHTS_STATUS_LABELS, rightsColor, rightsOutcome,
  type RightsPolicy, type RightsState, type RightsSummary,
} from '@prism/contracts';

/** 2026-10-31 14:00 (local), for an ISO instant. */
export function rightsInstant(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The state's label, with "ends soon" for a cleared or restricted asset whose rights end within the days. */
export function rightsStateText(r: Pick<RightsSummary, 'state' | 'endingSoon'>): string {
  return RIGHTS_STATE_LABELS[r.state] + (r.endingSoon && r.state !== 'ended' ? ' · ends soon' : '');
}

/** The mark's tooltip: the state, the dates, the holder and the restriction, one per line. */
export function rightsTooltip(r: RightsSummary): string {
  const lines = [`Rights: ${rightsStateText(r)}`];
  if (r.status && RIGHTS_STATUS_LABELS[r.status] !== RIGHTS_STATE_LABELS[r.state]) lines.push(`Status: ${RIGHTS_STATUS_LABELS[r.status]}`);
  if (r.from) lines.push(`Available from ${rightsInstant(r.from)}`);
  if (r.until) lines.push(`Available until ${r.until}`);
  if (r.holder) lines.push(`Holder: ${r.holder}`);
  if (r.restrictions) lines.push(`Restrictions: ${r.restrictions}`);
  if (r.credit) lines.push(`Credit: ${r.credit}`);
  if (r.fromSources) lines.push('Includes the sources it was made from');
  return lines.join('\n');
}

/** Whether a mark is shown: red or amber only (green, cleared, and unknown show nothing). */
export function rightsMarked(r: Pick<RightsSummary, 'state' | 'endingSoon'> | null | undefined): r is RightsSummary {
  if (!r) return false;
  const c = rightsColor(r);
  return c === 'red' || c === 'amber';
}

/**
 * The editor's top-bar pill over a sequence's sources: "Rights: 2 blocked", "Rights: 1 to override", "Rights: 3 to
 * confirm", "Rights: ends soon", or "Rights: cleared"; null without rights (rights off). The tone is its colour.
 */
export function sequenceRightsPill(list: ReadonlyArray<Pick<RightsSummary, 'state' | 'endingSoon'>>, policy: RightsPolicy | null | undefined):
  { text: string; tone: 'red' | 'amber' | 'green' | 'none'; state: RightsState } | null {
  if (!list.length) return null;
  let blocked = 0, override = 0, confirm = 0;
  let state: RightsState = 'cleared';
  for (const r of list) {
    if (RIGHTS_STATES.indexOf(r.state) < RIGHTS_STATES.indexOf(state)) state = r.state;
    const o = policy ? rightsOutcome(r.state, policy) : r.state === 'withdrawn' ? 'block' : null;
    if (o === 'block') blocked++;
    else if (o === 'override') override++;
    else if (o === 'confirm') confirm++;
  }
  if (blocked) return { text: `Rights: ${blocked} blocked`, tone: 'red', state };
  if (override) return { text: `Rights: ${override} to override`, tone: 'red', state };
  if (confirm) return { text: `Rights: ${confirm} to confirm`, tone: 'amber', state };
  if (list.some((r) => r.endingSoon)) return { text: 'Rights: ends soon', tone: 'amber', state };
  const tone = rightsColor({ state, endingSoon: false });
  return { text: `Rights: ${RIGHTS_STATE_LABELS[state].toLowerCase()}`, tone, state };
}
